import { useState } from 'react';
import { Method } from 'core/api/fetch';
import { useExperienceDetail } from 'core/contexts/experienceDetail';
import { Form, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Tabs } from '@utriper/core-ui';
import { Box, Button, CircularProgress, Grid2, Typography } from '@mui/material';
import { PRICE_TYPE, TAB_FORMS } from './styles';
import DetailsForm from './components/DetailsForm';
import UnitLinkFormFields from 'modules/Experience/components/UnitLinkFormFields';

const ExperienceDetailForm = ({ experienceId, experienceDetail, method = Method.POST, onClose = () => {} }) => {
    const { submitting, createExperienceDetail, updateExperienceDetail, translate } = useExperienceDetail();
    const isEdit = experienceDetail?.id ? true : false;
    const [submitted, setSubmitted] = useState(false);
    const [view, setView] = useState(TAB_FORMS.DETAILS);

    const tabOptions = [
        {
            id: TAB_FORMS.DETAILS,
            label: translate('tab_options.details'),
        },
        {
            id: TAB_FORMS.UNITS,
            label: translate('tab_options.units'),
        },
    ];

    const handleChangeView = (view) => setView(view);

    const getInitialValues = (experienceDetail) => ({
        ...experienceDetail,
        priceType: Number.isFinite(experienceDetail?.price) ? PRICE_TYPE.FIXED : Number.isFinite(experienceDetail?.priceGroupId) && experienceDetail?.priceGroupId > 0 ? PRICE_TYPE.PRICE_GROUP : PRICE_TYPE.INHERIT,
    });

    const validateForm = ({ description1, price, priceGroupId, currency, priceType }) => {
        const errors = {};

        if (!experienceId) {
            errors.general = translate('toasts.errors.invalid_experience');
        }

        if (!description1) {
            errors.description1 = translate('toasts.errors.missing_name');
        }

        if (priceType === PRICE_TYPE.FIXED && !Number.isFinite(price) && !currency) {
            errors.price = translate('toasts.errors.missing_price');
        }

        if (priceType === PRICE_TYPE.PRICE_GROUP && (!Number.isFinite(priceGroupId) || priceGroupId <= 0)) {
            errors.priceGroupId = translate('toasts.errors.missing_price_group_id');
        }

        if (!priceType) {
            errors.priceType = translate('toasts.errors.missing_price_type');
        }

        return errors;
    };

    const onSubmit = async (values) => {
        const priceType = parseInt(values.priceType);
        const valuesClone = { ...values };

        if (priceType === PRICE_TYPE.INHERIT) {
            valuesClone.price = null;
            valuesClone.currency = null;
            valuesClone.priceGroupId = null;
        }

        if (priceType === PRICE_TYPE.FIXED) {
            if (!values?.currency) {
                valuesClone.currency = 'USD';
            }
            valuesClone.priceGroupId = null;
        }

        if (priceType === PRICE_TYPE.PRICE_GROUP) {
            valuesClone.price = null;
            valuesClone.currency = null;
        }

        if (method === Method.POST) {
            await createExperienceDetail(experienceId, valuesClone);
        }

        if (method === Method.PATCH && experienceDetail?.id) {
            await updateExperienceDetail(experienceId, experienceDetail?.id, valuesClone);
        }

        onClose();
    };

    return (
        <>
            <Form
                key={`edit-experience-detail-${experienceId}`}
                onSubmit={onSubmit}
                validate={validateForm}
                initialValues={getInitialValues(experienceDetail)}
                mutators={{ ...arrayMutators }}
                render={({ handleSubmit, form }) => {
                    return (
                        <form key={`edit-experience-detail-${experienceId}`} onSubmit={handleSubmit} noValidate>
                            <Grid2 container spacing={1}>
                                {/* Title */}
                                <Grid2 container mt={2} width="100%" justifyContent="flex-start">
                                    <Typography sx={{ color: '#131313', fontSize: '2em', fontWeight: '600' }}>{method === Method.POST ? translate('title_post') : translate('title_edit')}</Typography>
                                </Grid2>

                                {/* Tabs */}
                                <Grid2 container size={12}>
                                    <Tabs
                                        value={view}
                                        options={tabOptions}
                                        onChange={handleChangeView}
                                        variant="scrollable"
                                        scrollButtons={false}
                                        sx={{
                                            '& .MuiTabs-indicator': { backgroundColor: '#131313' },
                                            '& .MuiButtonBase-root': { color: '#131313 !important', textTransform: 'none' },
                                        }}
                                    />
                                </Grid2>

                                {/* Form */}
                                <Grid2 container direction="column" width="100%" spacing={1}>
                                    {view === TAB_FORMS.DETAILS ? <DetailsForm experienceId={experienceId} form={form} submitted={submitted} /> : view === TAB_FORMS.UNITS ? <UnitLinkFormFields form={form} /> : ''}

                                    {/* Buttons */}
                                    <FormSpy subscription={{ dirty: true }}>
                                        {({ dirty }) => (
                                            <Grid2 container justifyContent="flex-end" spacing={1}>
                                                <Grid2>
                                                    <Button
                                                        onClick={(e) => {
                                                            onClose();
                                                            return form.reset(e);
                                                        }}
                                                        sx={{
                                                            fontWeight: '600',
                                                            color: '#131313',
                                                            textTransform: 'none',
                                                            border: 'solid #13131340 1px',
                                                        }}
                                                        variant="outlined"
                                                    >
                                                        {translate('buttons.cancel')}
                                                    </Button>
                                                </Grid2>
                                                <Grid2>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        sx={{
                                                            color: 'white !important',
                                                            backgroundColor: '#137DC5',
                                                            textTransform: 'none',
                                                            fontWeight: '600',
                                                        }}
                                                        disabled={!dirty}
                                                        loading={submitting}
                                                        loadingIndicator={
                                                            <Box sx={{ display: 'flex' }}>
                                                                <CircularProgress size={20} sx={{ color: '#131313bf' }} />
                                                            </Box>
                                                        }
                                                        onClick={(e) => {
                                                            setSubmitted(true);
                                                            return handleSubmit(e);
                                                        }}
                                                    >
                                                        {isEdit ? translate('buttons.update') : translate('buttons.create')}
                                                    </Button>
                                                </Grid2>
                                            </Grid2>
                                        )}
                                    </FormSpy>
                                </Grid2>
                            </Grid2>
                        </form>
                    );
                }}
            />
        </>
    );
};

export default ExperienceDetailForm;
